import type { DocumentGroup } from '@folio/components/module-elements/Documents/DocumentsCollection';
import {
  type DocumentViewerDocument,
  effectiveFraudStatus,
} from '@folio/components/features/document-viewer';
import type { ClaimManagementDocumentRefDto, DocumentFraudScoreDto } from 'korint-sdk';
import type { DocumentReviewFilter } from '../DocumentsUploadPolicyModule/document-review';

export const slotDocument = ({
  documentId,
  filename,
  libelle,
  attachedAt,
  category,
  generated,
}: ClaimManagementDocumentRefDto): DocumentViewerDocument | undefined =>
  documentId && attachedAt
    ? {
        id: documentId,
        name: filename ?? libelle,
        signedUrl: '',
        createdAt: attachedAt,
        type: generated ? 'generated' : 'supporting_document',
        category: category ?? null,
        metadata: {},
      }
    : undefined;

export const claimDocumentGroups = ({
  slots,
  documents,
}: {
  slots: readonly ClaimManagementDocumentRefDto[];
  documents: readonly DocumentViewerDocument[];
}): DocumentGroup<DocumentViewerDocument>[] => {
  const documentsById = new Map(documents.map(document => [document.id, document]));

  return slots.map((slot, index) => {
    // A slot holding a file the documents store didn't return still lists it, unviewable.
    const document = (slot.documentId && documentsById.get(slot.documentId)) || slotDocument(slot);
    return {
      id: `${slot.type}-${index}`,
      label: slot.libelle,
      requirements: slot.description,
      required: !slot.generated,
      documents: document ? [document] : [],
    };
  });
};

type ClaimDocumentGroup = DocumentGroup<DocumentViewerDocument>;

const isMissing = ({ required, documents }: ClaimDocumentGroup) =>
  Boolean(required) && documents.length === 0;

export const claimDocumentReview = ({
  groups,
  scoresByDocumentId,
  filter,
}: {
  groups: readonly ClaimDocumentGroup[];
  scoresByDocumentId: ReadonlyMap<string, DocumentFraudScoreDto>;
  filter: DocumentReviewFilter;
}) => {
  const documents = groups.flatMap(({ documents }) => documents);
  const statusOf = ({ id }: DocumentViewerDocument) => {
    const score = scoresByDocumentId.get(id);
    return score && effectiveFraudStatus(score);
  };
  const needsAttention = (document: DocumentViewerDocument) =>
    ['review_needed', 'fraud'].includes(statusOf(document) ?? '');

  return {
    groups:
      filter === 'missing'
        ? groups.filter(isMissing)
        : filter === 'attention'
          ? groups.map(group => ({ ...group, documents: group.documents.filter(needsAttention) }))
          : [...groups],
    counts: {
      receivedCount: documents.length,
      missingRequiredCount: groups.filter(isMissing).length,
      attentionCount: documents.filter(needsAttention).length,
      processingCount: documents.filter(document => statusOf(document) === 'pending').length,
    },
  };
};
